import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import useHttpClient from './useHttpClient';
import IProjectContact from './models/projectContact';

interface IProjectContacts {
  count: number;
  offset: number;
  limit: number;
  hasMore: boolean;
  requestedFields: string;
  items: IProjectContact[];
}

interface IProjectContactsPayload {
  loading: boolean;
  error?: Error;
  contacts?: IProjectContacts;
  called: boolean;
}

export interface IQueryProjectContactsHook {
  projectContactsSearchResults: IProjectContactsPayload;
  getProjectContacts: (
    projectId: number, searchTerm: string, limit?: number
  ) => void;

  refetchProjectContacts: () => void;
}

const useQueryProjectContacts = (): IQueryProjectContactsHook => {
  const httpClient = useHttpClient();
  const [searchParams, setSearchParams] = useState<{
    projectId: number;
    searchTerm: string;
    limit: number;
  } | null>(null);

  const { data, error, isLoading, refetch } = useInfiniteQuery({
    queryKey: ['projectContacts', searchParams?.projectId, searchParams?.searchTerm, searchParams?.limit],
    queryFn: async ({ pageParam = 0 }) => {
      if (!searchParams) {
        throw new Error('Search parameters not set');
      }
      const { projectId, searchTerm, limit } = searchParams;
      const params = new URLSearchParams({
        limit: limit.toString(),
        searchTerm,
        offset: pageParam.toString(),
      });
      const response = await httpClient.get(`/projects/${projectId}/contacts?${params.toString()}`);
      return response.data as IProjectContacts;
    },
    getNextPageParam: (lastPage: IProjectContacts, allPages) => {
      return lastPage.hasMore ? allPages.reduce((total, page) => total + page.items.length, 0) : undefined;
    },
    enabled: searchParams !== null,
    staleTime: 0,
    cacheTime: 0,
  });

  const getProjectContacts = useCallback((projectId: number, searchTerm: string, limit: number = 50) => {
    setSearchParams({ projectId, searchTerm, limit });
  }, []);


  const refetchProjectContacts = useCallback(() => {
    refetch();
  }, [refetch]);

  // Only get the first page data since we're not actually paginating
  const firstPage = data?.pages[0];

  const contacts: IProjectContacts | undefined = firstPage ? {
    count: firstPage.count,
    offset: firstPage.offset,
    limit: firstPage.limit,
    hasMore: firstPage.hasMore, // Use API's hasMore directly
    requestedFields: firstPage.requestedFields,
    items: firstPage.items,
  } : undefined;

  return {
    projectContactsSearchResults: {
      error: error as Error,
      loading: isLoading,
      contacts,
      called: searchParams !== null,
    },
    getProjectContacts,
    refetchProjectContacts,

  };
};

export default useQueryProjectContacts;
